import { getBootstrapData } from "@swa-ui/bootstrap";
import { window } from "@swa-ui/browser/window";

import { getPersistedCarData, resetPersistedCarData, setPersistedCarData } from "../carPersistence";
import { CAR_TRAWLER_SCRIPT_ELEMENT_ID, CAR_TRAWLER_SECTION_ID } from "../defines";

const BEHAVIOR_CROSS_SELL = "cross-sell";
const BEHAVIOR_INPATH = "inpath";
const BEHAVIOR_SBXS = "sbxs";
const CONTEXT_SEARCH = "search";
const CONTEXT_INPATH = "inpath";
const PAGE_TYPE_PRIMARY = "primary";
const PAGE_TYPE_SECONDARY = "secondary";
const PROD_HOSTNAME_REGEX = /^(www|espanol|mobile|mobile-offline)\.(southwest|swabiz)\.com$/;
const SECTION_TYPE_SEARCH = "search";

/**
 * @typedef {Object} Options
 * @property {Boolean} autoNavigateOnSubmit Indicates if submit action should navigate to vendor page or emit an event instead.
 * @property {object} flight Flight details for all legs.
 * @property {Function} onSubmit Custom event handler for submit action per consumer.
 * @property {object} passengers Passengers count for various passenger type.
 * @property {String} sectionType Type of UI to be displayed (Search Widget, Select cars grid, Cross sell view etc).
 * @property {Boolean} shouldInitializeWithPersistedData Indicates if CTStore should use existing data in session storage.
 * @property {Boolean} shouldSetSessionData Indicates if user selections should be saved in session storage on submit.
 * @property {Array} suppliersAllowed Array of allowed car rental suppliers.
 */

/**
 * This function sets all the required elements needed for rendering a Smartblock.
 * @param {Options} options
 */
export const loadCarSection = (options) => {
  setupCTStore(options);
  loadSmartBlockScript(options);
  setupCTEvents(options);
};

/**
 * CTStore is a window object which needs to be present before Smartblock script is executed.
 * The Smartblock script updates this object automatically on user actions.
 * @param {Options} options
 */
const setupCTStore = (options = {}) => {
  const {
    autoNavigateOnSubmit = false,
    flight = {},
    passengers = {},
    shouldInitializeWithPersistedData = false,
    sectionType = SECTION_TYPE_SEARCH,
    suppliersAllowed,
  } = options;
  const storeData = {
    dataSource: {
      currency: "USD",
      custom: {
        behavior: getBehavior(autoNavigateOnSubmit, sectionType),
        context: sectionType === SECTION_TYPE_SEARCH ? CONTEXT_SEARCH : CONTEXT_INPATH,
        pageType: autoNavigateOnSubmit ? PAGE_TYPE_SECONDARY : PAGE_TYPE_PRIMARY,
        suppliersAllowed,
      },
      flight,
      language: window.MP?.UrlLang ?? "EN",
      passengers,
    },
  };

  window.CTStore
    ? (window.CTStore.dataSource = storeData.dataSource)
    : (window.CTStore = storeData);

  if (shouldInitializeWithPersistedData) {
    window.CTStore.dataSource = getPersistedCarData();
  } else {
    resetPersistedCarData();
  }
};

const getBehavior = (autoNavigateOnSubmit, sectionType) => {
  let behavior = BEHAVIOR_CROSS_SELL;

  if (sectionType === SECTION_TYPE_SEARCH) {
    if (autoNavigateOnSubmit) {
      behavior = BEHAVIOR_SBXS;
    } else {
      behavior = BEHAVIOR_INPATH;
    }
  }

  return behavior;
};

/**
 * This function creates the HTML script element with required attributes and adds it to the DOM
 * @param {Options} options
 */
const loadSmartBlockScript = (options = {}) => {
  const { waitForElement } = options;
  const doc = window.document;
  const existingScriptId = doc.getElementById(CAR_TRAWLER_SCRIPT_ELEMENT_ID);
  const { CARTRAWLER_SCRIPT_SRC_NON_PROD, CARTRAWLER_SCRIPT_SRC_PROD } = getBootstrapData("urls");
  const scriptSource = isProd() ? CARTRAWLER_SCRIPT_SRC_PROD : CARTRAWLER_SCRIPT_SRC_NON_PROD;

  if (!existingScriptId) {
    const script = doc.createElement("script");

    script.type = "text/javascript";
    script.src = scriptSource;
    script.async = true;
    script.id = CAR_TRAWLER_SCRIPT_ELEMENT_ID;
    script.defer = true;
    script.onerror = function () {
      console.error("Failed to load the Smartblock script.");
    };

    if (!window.loading_ct_smartblock) {
      window.loading_ct_smartblock = true;
      waitForElement ? lookForSmartBlock(script) : executeSmartBlockScript(script);
    }
  } else {
    waitForElement ? lookForSmartBlock() : executeSmartBlockScript();
  }
};

/**
 * Passively looks for the Smartblock element in the DOM.
 * This guarantees that the script is executed only after the element is commited to the DOM first, outside of React lifecycles.
 * @param {*} script
 */
const lookForSmartBlock = (script) => {
  // TODO: Add a timeout if element is not found for longer than expected time. (Probably 100ms)
  const doc = window.document;
  const domObserver = new MutationObserver((_, observer) => {
    const smartblock = doc.getElementById(CAR_TRAWLER_SECTION_ID);

    if (smartblock) {
      observer.disconnect();
      executeSmartBlockScript(script);
    }
  });

  domObserver.observe(doc.body, { childList: true, subtree: true });
};

/**
 * This appends the JS script in the DOM. ALternatively, it restarts the vendor SBIP function if script is not provided in the argument.
 * Both the options result in execution of the Smartblock script.
 * @param {*} script
 */
const executeSmartBlockScript = (script) => {
  const doc = window.document;

  if (script) {
    doc.head.appendChild(script);
  } else {
    window.CTStore?.restartSbip?.();
  }
};

const setupCTEvents = (options = {}) => {
  // TODO: Air in-path would need to use one or more of the events mentioned below. SPASHOP-572
  const { onSubmit, shouldSetSessionData } = options;

  !window.swa_ct_event_listener_added &&
    window.addEventListener("ct-smartblock-event", function (event) {
      const { payload, type } = event.detail;

      switch (type) {
        case "GET_ADDON":
          shouldSetSessionData && setPersistedCarData(payload?.dataSource);
          onSubmit?.();

          break;
        // case "ADD_ADDON":
        //   // implement event handler, if needed for a use case. handleCarAddedToBasket(payload);
        //   break;
        // case "REMOVE_ADDON":
        //   // implement event handler, if needed for a use case. handleCarRemovedFromBasket(payload);
        //   break;
        // case "LOAD_EVENT":
        //   // implement event handler, if needed for a use case. handleWidgetLoaded();
        //   break;
        // case "BACK_TO_EXTRAS":
        //   // implement event handler, if needed for a use case. handleBackToExtras(payload);
        //   break;
      }
    });

  window.swa_ct_event_listener_added = true;
};

// TODO: Explore using getEnvironmentTier from swa-ui-dotcom package when the cars package is used only by Nextgen SPAs. HYPE-1330
const isProd = () => {
  const { hostname } = window?.location ?? { hostname: "www.southwest.com" };

  return PROD_HOSTNAME_REGEX.test(hostname);
};
